import React, { useState } from "react";
import styled from "styled-components";

type DonutChartProps = {
  cx: number;
  cy: number;
  radius: number;
  data: { color: string; value: number; label: string }[];
};

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  flex-direction: row;
  margin-top: 15px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  margin: 5px 10px;
`;

const ColorBox = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  background-color: ${({ color }) => color};
  margin-right: 8px;
  border-radius: 2px;
`;

const LabelText = styled.span`
  font-size: 12px;
  font-weight: bold;
  margin-right: 10px;
`;

const ValueText = styled.span`
  font-size: 12px;
`;

const DonutChart: React.FC<DonutChartProps> = ({ cx, cy, radius, data }) => {
  const ageOrder = ["0-18", "18-25", "25-35", "35-45", "45-60", "60+"];

  // Sort by age order instead of value
  const sortedData = [...data].sort((a, b) => ageOrder.indexOf(a.label) - ageOrder.indexOf(b.label));
  const totalValue = sortedData.reduce((sum, item) => sum + item.value, 0);
  const maxItem = Math.max(...sortedData.map((item) => item.value));

  const [selectedLabel, setSelectedLabel] = useState(sortedData[0].label);
  const selectedValue = sortedData.find(d => d.label === selectedLabel)?.value ?? 0;

  let accumulatedPercentage = 0;

  return (
    <ChartContainer>
      <svg width="200" height="200" viewBox="0 0 200 200">
        {sortedData.map((item, index) => {
          const percentage = item.value / totalValue;
          const circumference = 2 * Math.PI * radius;
          const strokeDasharray = `${circumference * percentage} ${circumference * (1 - percentage)}`;
          const strokeDashoffset = circumference * accumulatedPercentage;
          const strokeWidth = (item.value / maxItem) * 30 + 5; // restore dynamic thickness
          accumulatedPercentage -= percentage;

          return (
            <circle
              key={index}
              cx={cx}
              cy={cy}
              r={radius}
              stroke={item.color}
              strokeWidth={strokeWidth}
              fill="none"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 100 100)"
              style={{ cursor: "pointer", transition: "all 0.3s ease" }}
              onClick={() => setSelectedLabel(item.label)}
            />
          );
        })}

        {/* Center text shows selected value percentage */}
        <text
          x={cx}
          y={cy - 10}
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize="14"
          fontWeight="bold"
          fill="#333"
        >
          {selectedLabel}
        </text>
        <text
          x={cx}
          y={cy + 10}
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize="12"
          fill="#555"
        >
          {selectedValue.toFixed(2)}%
        </text>

      </svg>

      {/* Legend */}
      <LegendContainer>
        {sortedData.map((item, index) => (
          <LegendItem key={index}>
            <ColorBox color={item.color} />
            <LabelText>{item.label}</LabelText>
            <ValueText>{item.value}</ValueText>
          </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};

export default DonutChart;
